import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI } from '../services/api';
import { disconnectSocket } from '../services/chatSocket';
import useIdleTimer from '../hooks/useIdleTimer';
import IdleWarningModal from '../components/shared/IdleWarningModal';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [discordInviteUrl, setDiscordInviteUrl] = useState(null);
  const [telegramInviteUrl, setTelegramInviteUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const isMounted = React.useRef(true);

  useEffect(() => {
    isMounted.current = true;
    return () => { isMounted.current = false; };
  }, []);

  const logout = useCallback(async () => {
    try {
      await authAPI.logout();
    } catch (err) {
      // Silently fail
    } finally {
      sessionStorage.removeItem('accessToken');
      disconnectSocket();
      setUser(null);
      setSubscription(null);
      setDiscordInviteUrl(null);
      setTelegramInviteUrl(null);
      setLoading(false);
    }
  }, []);

  // FIXED: Wrap loadUser in useCallback for stable reference in useEffect deps
  const loadUser = useCallback(async () => {
    let token = sessionStorage.getItem('accessToken');

    // SECURITY: If no token in sessionStorage (e.g., after tab close or page refresh),
    // attempt a silent token refresh using the httpOnly refresh cookie.
    // This is step 1 toward full memory-only token storage — the ideal final state is to
    // remove sessionStorage entirely and always rely on this refresh path.
    // TODO: Remove sessionStorage reads/writes once all API clients (mobile + web)
    //       are confirmed to use the refresh-cookie flow.
    if (!token) {
      try {
        const { data: refreshData } = await authAPI.refresh();
        if (refreshData?.success && refreshData?.data?.accessToken) {
          token = refreshData.data.accessToken;
          sessionStorage.setItem('accessToken', token);
        } else {
          if (isMounted.current) setLoading(false);
          return;
        }
      } catch {
        // No valid refresh cookie — user is genuinely not logged in
        if (isMounted.current) setLoading(false);
        return;
      }
    }

    try {
      const { data } = await authAPI.getMe();
      if (!isMounted.current) return;
      if (data.success && data.data) {
        setUser(data.data.user);
        setSubscription(data.data.subscription);
        setDiscordInviteUrl(data.data.discordInviteUrl || null);
        setTelegramInviteUrl(data.data.telegramInviteUrl || null);
      } else {
        // Only logout if success is explicitly false and not handled by interceptor
        await logout();
      }
    } catch (err) {
      // If 401, the interceptor in api.js should handle refresh.
      // We only explicitly logout if the server tells us the session is invalid or forbidden
      if (err.response?.status === 403 || err.response?.data?.code === 'USER_NOT_FOUND') {
        await logout();
      }
      // We do NOT logout on 429 (Rate Limit), 500 (Server Error), or Network Error.
    } finally {
      if (isMounted.current) setLoading(false);
    }
  }, [logout]);

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  const [showIdleWarning, setShowIdleWarning] = useState(false);
  const [idleCountdown, setIdleCountdown] = useState(30);

  const { pause: pauseIdleTimer, resume: resumeIdleTimer } = useIdleTimer(() => {
    if (user && !showIdleWarning) {
      pauseIdleTimer();
      setShowIdleWarning(true);
      setIdleCountdown(30);
    }
  }, 180000); // 3 minutes

  useEffect(() => {
    let interval;
    if (showIdleWarning) {
      interval = setInterval(() => {
        setIdleCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(interval);
            logout();
            window.location.href = '/login?reason=idle';
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [showIdleWarning, logout]);

  const handleImHere = () => {
    setShowIdleWarning(false);
    setIdleCountdown(30);
    resumeIdleTimer();
  };

  const login = async (credentials) => {
    try {
      const { data } = await authAPI.login(credentials);
      if (data.success && data.data) {
        sessionStorage.setItem('accessToken', data.data.accessToken);
        // refresh token is sent as httpOnly cookie by the server
        setUser(data.data.user);
        setSubscription(data.data.subscription);
        setDiscordInviteUrl(data.data.discordInviteUrl || null);
        setTelegramInviteUrl(data.data.telegramInviteUrl || null);
        return data.data;
      } else {
        throw new Error(data.message || 'Login failed');
      }
    } catch (err) {
      throw err;
    }
  };

  const register = async (userData) => {
    try {
      const { data } = await authAPI.register(userData);
      if (data.success) {
        // If server returned tokens (legacy behavior), store them.
        if (data.data && data.data.accessToken) {
          sessionStorage.setItem('accessToken', data.data.accessToken);
          setUser(data.data.user);
          setSubscription(data.data.subscription);
          setDiscordInviteUrl(data.data.discordInviteUrl || null);
          setTelegramInviteUrl(data.data.telegramInviteUrl || null);
        }
        return data;
      } else {
        throw new Error(data.message || 'Registration failed');
      }
    } catch (err) {
      throw err;
    }
  };

  const updateUser = useCallback((updates) => setUser(prev => prev ? ({ ...prev, ...updates }) : null), []);
  const setSub = useCallback((sub) => setSubscription(sub), []);

  const isAdmin = user?.role === 'admin';
  const isAgent = user?.role === 'agent';
  const planSlug = (isAdmin || isAgent) ? 'vip' : (subscription?.plan_slug || 'free');

  return (
    <AuthContext.Provider value={{
      user, subscription, discordInviteUrl, telegramInviteUrl, loading, isAdmin, isAgent, planSlug,
      login, register, logout, updateUser, updateSubscription: setSub, loadUser
    }}>
      {children}
      <IdleWarningModal 
        isOpen={showIdleWarning} 
        countdown={idleCountdown} 
        onConfirm={handleImHere} 
      />
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
